<template>
  <div
    class="continuous-globe"
    :class="{ 'continuous-globe--active': active }"
    :style="globeStyle"
    aria-hidden="true"
  >
    <span class="continuous-globe__halo continuous-globe__halo--outer"></span>
    <span class="continuous-globe__halo continuous-globe__halo--inner"></span>
    <img class="continuous-globe__source" :src="globeImageSrc" alt="" />
    <canvas ref="canvasRef" class="continuous-globe__canvas"></canvas>
  </div>
</template>

<script lang="ts">
import {
  AmbientLight,
  AdditiveBlending,
  BackSide,
  CanvasTexture,
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshPhysicalMaterial,
  PerspectiveCamera,
  PointLight,
  RepeatWrapping,
  RingGeometry,
  Scene,
  SphereGeometry,
  TorusGeometry,
  WebGLRenderer,
} from 'three';
import { defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';

const createGlobeTexture = (): any => {
  const size = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size / 2;
  const context = canvas.getContext('2d');

  if (!context) {
    return new CanvasTexture(canvas);
  }

  const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#0a2b70');
  gradient.addColorStop(0.5, '#1aa4ff');
  gradient.addColorStop(1, '#42188a');
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.globalCompositeOperation = 'screen';
  context.strokeStyle = 'rgba(188, 242, 255, 0.36)';
  context.lineWidth = 2;

  for (let y = 54; y < canvas.height; y += 72) {
    context.beginPath();
    for (let x = 0; x <= canvas.width; x += 18) {
      const wave = Math.sin((x + y * 1.7) * 0.018) * 16;
      if (x === 0) {
        context.moveTo(x, y + wave);
      } else {
        context.lineTo(x, y + wave);
      }
    }
    context.stroke();
  }

  const nodes = [
    [118, 116, 30],
    [226, 262, 22],
    [352, 178, 28],
    [476, 328, 24],
    [594, 130, 26],
    [724, 258, 34],
    [842, 178, 22],
    [948, 342, 30],
  ];

  context.font = '600 30px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  nodes.forEach(([x, y, radius], index) => {
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fillStyle = 'rgba(229, 251, 255, 0.16)';
    context.fill();
    context.strokeStyle = 'rgba(209, 250, 255, 0.7)';
    context.stroke();

    context.fillStyle =
      index % 2 ? 'rgba(242, 252, 255, 0.86)' : 'rgba(159, 232, 255, 0.86)';
    context.fillText(
      index % 3 === 0 ? '%' : index % 3 === 1 ? '+' : 'AI',
      x,
      y + 1
    );
  });

  for (let i = 0; i < 82; i += 1) {
    const x = (i * 173) % canvas.width;
    const y = 36 + ((i * 91) % (canvas.height - 72));
    const radius = 2 + (i % 5);
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fillStyle = `rgba(226, 252, 255, ${0.28 + (i % 4) * 0.1})`;
    context.fill();
  }

  context.globalCompositeOperation = 'source-over';
  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(1, 1);
  return texture;
};

export default defineComponent({
  name: 'AiContinuousGlobe',
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    const globeStyle = ref<Record<string, string>>({});
    const globeImageSrc = `${process.env.BASE_URL}login-original-globe.png`;
    let renderer: any = null;
    let scene: any = null;
    let camera: any = null;
    let globe: any = null;
    let ringGroup: any = null;
    let texture: any = null;
    let resizeObserver: ResizeObserver | null = null;
    let animationFrameId = 0;

    const updateGlobeBounds = (): void => {
      const viewportWidth = window.innerWidth || 1;
      const viewportHeight = window.innerHeight || 1;
      const sourceWidth = 1138;
      const sourceHeight = 640;
      const cropLeft = 318;
      const cropTop = 34;
      const cropSize = 502;
      const scale = Math.max(
        viewportWidth / sourceWidth,
        viewportHeight / sourceHeight
      );
      const renderedWidth = sourceWidth * scale;
      const renderedHeight = sourceHeight * scale;
      const offsetX = (viewportWidth - renderedWidth) / 2;
      const offsetY = (viewportHeight - renderedHeight) / 2;

      globeStyle.value = {
        height: `${Math.round(cropSize * scale)}px`,
        left: `${Math.round(offsetX + cropLeft * scale)}px`,
        top: `${Math.round(offsetY + cropTop * scale)}px`,
        width: `${Math.round(cropSize * scale)}px`,
      };
    };

    const resizeRenderer = (): void => {
      const canvas = canvasRef.value;

      if (!canvas || !renderer || !camera) {
        return;
      }

      const rect = canvas.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      updateGlobeBounds();
    };

    const renderFrame = (): void => {
      if (!renderer || !scene || !camera || !globe || !ringGroup || !texture) {
        return;
      }

      const seconds = performance.now() * 0.001;
      globe.rotation.y = seconds * 0.42;
      globe.rotation.x = 0.1 + Math.sin(seconds * 0.45) * 0.035;
      texture.offset.x = (seconds * 0.035) % 1;
      ringGroup.rotation.z = seconds * 0.16;
      ringGroup.rotation.y = Math.sin(seconds * 0.28) * 0.12;

      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(renderFrame);
    };

    onMounted(() => {
      const canvas = canvasRef.value;

      if (!canvas) {
        return;
      }

      scene = new Scene();
      camera = new PerspectiveCamera(36, 1, 0.1, 100);
      camera.position.set(0, 0, 5);

      renderer = new WebGLRenderer({
        alpha: true,
        antialias: true,
        canvas,
        preserveDrawingBuffer: true,
        powerPreference: 'high-performance',
      });
      renderer.setClearColor(new Color(0x000000), 0);

      texture = createGlobeTexture();
      const globeMaterial = new MeshPhysicalMaterial({
        color: 0x9ceeff,
        emissive: 0x1f71ff,
        emissiveIntensity: 0.18,
        envMapIntensity: 0.8,
        map: texture,
        metalness: 0.18,
        opacity: 0.22,
        roughness: 0.28,
        transparent: true,
      });
      globe = new Mesh(new SphereGeometry(1.18, 96, 64), globeMaterial);
      scene.add(globe);

      const atmosphere = new Mesh(
        new SphereGeometry(1.25, 96, 64),
        new MeshBasicMaterial({
          color: 0x86f0ff,
          transparent: true,
          opacity: 0.17,
          blending: AdditiveBlending,
          side: BackSide,
        })
      );
      scene.add(atmosphere);

      ringGroup = new Group();
      const ringMaterial = new MeshBasicMaterial({
        color: 0xa8f6ff,
        transparent: true,
        opacity: 0.38,
        blending: AdditiveBlending,
        side: DoubleSide,
      });
      const orbitOne = new Mesh(
        new TorusGeometry(1.42, 0.012, 12, 180),
        ringMaterial
      );
      orbitOne.rotation.x = 1.2;
      const orbitTwo = new Mesh(
        new RingGeometry(1.34, 1.35, 180),
        ringMaterial
      );
      orbitTwo.rotation.x = 1.46;
      orbitTwo.rotation.z = 0.18;
      ringGroup.add(orbitOne, orbitTwo);
      scene.add(ringGroup);

      scene.add(new AmbientLight(0xbfeeff, 1.6));
      const keyLight = new PointLight(0xffffff, 2.8, 8);
      keyLight.position.set(-2.6, 2.2, 4);
      scene.add(keyLight);
      const rimLight = new PointLight(0x69dbff, 3.2, 7);
      rimLight.position.set(2.8, -0.4, 3);
      scene.add(rimLight);

      resizeRenderer();

      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(resizeRenderer);
        resizeObserver.observe(canvas);
      }

      window.addEventListener('resize', resizeRenderer);
      renderFrame();
    });

    onBeforeUnmount(() => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      window.removeEventListener('resize', resizeRenderer);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      texture?.dispose();
      globe?.geometry.dispose();
      globe?.material.dispose();
      ringGroup?.children.forEach((child: any) => {
        const mesh = child as any;
        mesh.geometry?.dispose();
      });
      renderer?.dispose();
    });

    return {
      canvasRef,
      globeImageSrc,
      globeStyle,
    };
  },
});
</script>

<style scoped>
.continuous-globe {
  position: fixed;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  transform: scale(0.99);
  transform-origin: center center;
  transition:
    opacity 0.6s ease,
    transform 0.6s ease;
}

.continuous-globe--active {
  opacity: 0.92;
  transform: scale(1);
}

.continuous-globe__source {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0.96;
  filter: drop-shadow(0 0 18px rgba(105, 218, 255, 0.34))
    drop-shadow(0 14px 24px rgba(104, 93, 255, 0.18));
}

.continuous-globe__canvas {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: block;
  width: 100%;
  height: 100%;
  opacity: 0.3;
  mix-blend-mode: screen;
  filter: drop-shadow(0 0 16px rgba(91, 213, 255, 0.34))
    drop-shadow(0 14px 24px rgba(92, 83, 255, 0.18));
}

.continuous-globe__halo {
  position: absolute;
  inset: 15%;
  z-index: 1;
  border-radius: 999px;
  background: radial-gradient(
    circle,
    rgba(158, 245, 255, 0.3) 0%,
    rgba(89, 162, 255, 0.18) 34%,
    rgba(107, 70, 255, 0) 68%
  );
  filter: blur(18px);
}

.continuous-globe__halo--outer {
  inset: 3%;
  opacity: 0.65;
  filter: blur(34px);
}

.continuous-globe__halo--inner {
  inset: 22%;
  opacity: 0.72;
}

@media (max-width: 900px) {
  .continuous-globe--active {
    opacity: 0.86;
  }

  .continuous-globe__canvas {
    opacity: 0.22;
  }
}
</style>
